import {
  Badge,
  Button,
  ConfirmationModalContent,
  ModalPopup,
  ReeligibilityInfo,
  Stack
} from 'components';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { setChangeStatusModal } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import { notifyUserError } from 'utils/notifications';
import { PERMISSION } from 'utils/protectedRoutes/userRolesAndPermissionTypes';

import { ChangeStatusModal } from './changeStatusModal';
import { ManagerReview } from './managerReview';
import style from './profileInfo.module.scss';
import { Screener } from './screener';
import { Snapshot } from './snapshot';
import { HealthTracker } from './healthTracker';
import { MedicalVisits } from './medicalVisits';
import { Files } from './files';
import NewManagerReview from './managerReview/newManagerReview';

const ProfileInfo = () => {
  const { client, changeStatusModal } = useAppSelector((state) => state.clients);
  const { data } = client;
  const { isValid, dirty, submitForm, resetForm } = useFormikContext();
  const { enableBillingUpdates, statusMgmtUpdates2, enableFileUpload } = useFlags();
  const dispatch = useAppDispatch();
  const { clientId } = useParams();
  const isEdit = Boolean(clientId);
  const age = dayjs().diff(dayjs(client.data?.dob), 'year');
  const dob = dayjs(client.data?.dob).format('MM/DD/YYYY');
  const canUpdateManagerReview = useIsOnlyVisibleToUserWithPermissions([
    PERMISSION.UPDATE_MANAGER_REVIEW
  ]);

  const canViewMedicalVisits = enableFileUpload
    ? data?.medicalVisitsEnabled
    : data?.clinicalEncountersEnabled;

  const handleUpdateStatusClick = () => {
    if (!isValid || !clientId) {
      notifyUserError(
        'Please complete all required fields on the profile tab prior to changing status.'
      );
      return;
    } else if (dirty) {
      dispatch(setChangeStatusModal('confirm'));
    } else {
      dispatch(setChangeStatusModal('changeStatus'));
    }
  };

  const handleCloseModal = () => {
    dispatch(setChangeStatusModal(null));
  };

  const handleConfirm = async () => {
    await submitForm();
    dispatch(setChangeStatusModal('changeStatus'));
  };

  const handleReject = () => {
    dispatch(setChangeStatusModal('changeStatus'));
    resetForm();
  };

  return (
    <>
      <Stack mt="26px" data-test-id="profile-info-name">
        <p className={style.clientName}>
          {isEdit ? `${data?.firstName} ${data?.lastName}` : 'New Client'}
        </p>
      </Stack>
      {clientId && (
        <Stack display="flex" alignItems="center" gap="8px">
          <p className={style.clientId}>DOB: {`${dob} (${age})`}</p>
          <p className={style.clientId}>
            {
              data?.primaryPhoneNumber
                // ?.replace(/([(])/g, '')
                ?.replace(/([)])/g, ') ')
              // ?.replace(/\s/g, '')
            }
          </p>
          <p className={style.clientId}>IMPaCT ID: {data?.prependedId}</p>
        </Stack>
      )}
      <Stack direction="row" alignItems="center" sx={{ lineHeight: '14px' }}>
        {data?.status?.id === 'PENDING' ? (
          <Badge text={data?.status?.name} type="warning" />
        ) : (
          <p className={style.status}>{isEdit ? data?.status?.name : 'Pre-Outreach'}</p>
        )}
        {statusMgmtUpdates2 && data?.reEligibility?.available && (
          <ReeligibilityInfo
            text={`On ${dayjs(data?.reEligibility?.date).format(
              'MM/DD/YYYY'
            )} this client became eligible for ${data?.reEligibility?.programName}`}
            padding="0 0 0 8px"
          />
        )}
      </Stack>
      {data?.safetyFlag && (
        <div className={style.safety}>
          <span className="material-icons">warning_amber</span>
          Safety
        </div>
      )}
      <Stack gap="20px" width="100%" display="flex" alignItems="center">
        <Stack display="flex" gap="8px" alignItems="center">
          <Stack width="147px" data-test-id="profile-update-status">
            <Button
              theme="secondary"
              text="Update status"
              handleOnClick={handleUpdateStatusClick}
            />
          </Stack>
          {canUpdateManagerReview && (
            <Stack width="136px" data-test-id="profile-update-status">
              {enableBillingUpdates ? <NewManagerReview /> : <ManagerReview />}
            </Stack>
          )}
        </Stack>
        <Stack gap="16px" display="flex" width="100%">
          {isEdit && <Snapshot />}
          {isEdit && <Screener />}
          {isEdit && <HealthTracker />}
          {isEdit && canViewMedicalVisits && <MedicalVisits />}
          {isEdit && enableFileUpload && <Files />}
        </Stack>
      </Stack>

      <ModalPopup
        handleClose={handleCloseModal}
        open={changeStatusModal === 'confirm'}
        children={
          <ConfirmationModalContent
            title="You have unsaved changes"
            rejectButtonText="Discard changes"
            confirmButtonText="Save changes"
            handleConfirm={handleConfirm}
            handleReject={handleReject}
          />
        }
      />
      <ModalPopup
        customClassInsideContainer={style.modal}
        open={changeStatusModal === 'changeStatus'}
        handleClose={handleCloseModal}
      >
        <ChangeStatusModal onClose={handleCloseModal} />
      </ModalPopup>
    </>
  );
};

export default ProfileInfo;
